import React from 'react';
import type { CustomerStateHistory } from '../types/customers.types';
import { CustomerStateBadge } from '../../customer-states/components/CustomerStateBadge';
import { formatSeparatorDate } from '../../whatsapp/utils/date.util';

interface CustomerStateTimelineProps {
  historyData: { data: CustomerStateHistory[]; nextCursor: string | null };
  isLoading: boolean;
  isFetchingMore?: boolean;
}

const getDotClass = (type?: string) => {
  switch (type) {
    case 'INITIAL':
      return { class: 'bg-success-subtle text-success', icon: 'ri-play-line' };
    case 'FINAL':
      return { class: 'bg-danger-subtle text-danger', icon: 'ri-check-line' };
    case 'TRANSIENT':
      return { class: 'bg-warning-subtle text-warning', icon: 'ri-route-line' };
    default:
      return { class: 'bg-light text-muted', icon: 'ri-checkbox-blank-circle-fill' };
  }
};

export const CustomerStateTimeline: React.FC<CustomerStateTimelineProps> = ({
  historyData,
  isLoading,
  isFetchingMore,
}) => {
  if (isLoading) {
    return (
      <div className="acitivity-timeline placeholder-glow p-2">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="acitivity-item d-flex mb-4">
            <div className="flex-shrink-0">
              <div className="avatar-xs acitivity-avatar">
                <span className="avatar-title placeholder bg-secondary rounded-circle"></span>
              </div>
            </div>
            <div className="flex-grow-1 ms-3">
              <div className="placeholder bg-secondary col-4 mb-2" style={{ height: '10px' }}></div>
              <div className="placeholder bg-secondary col-8 d-block" style={{ height: '15px' }}></div>
              <div className="placeholder bg-secondary col-5 mt-1 d-block" style={{ height: '10px' }}></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!historyData || !historyData.data || historyData.data.length === 0) {
    return (
      <div className="text-center text-muted my-5 py-4">
        <i
          className="ri-history-line fs-36 text-muted mb-2 d-block"
          style={{ opacity: 0.5 }}
        ></i>
        <p className="mb-0 fs-14">No hay historial de cambios de estado para este cliente.</p>
      </div>
    );
  }

  return (
    <div className="acitivity-timeline p-2">
      {historyData.data.map((item) => (
        <div key={item.id} className="acitivity-item d-flex mb-4">
          <div className="flex-shrink-0">
            <div className="avatar-xs acitivity-avatar">
              <div className={`avatar-title rounded-circle ${getDotClass(item.toStateType).class}`}>
                <i className={`${getDotClass(item.toStateType).icon} fs-12`}></i>
              </div>
            </div>
          </div>
          <div className="flex-grow-1 ms-3">
            <div className="d-flex justify-content-between align-items-center mb-1">
              <span className="text-muted fs-11">
                <i className="ri-calendar-event-line me-1"></i>
                {formatSeparatorDate(item.createdAt)}
              </span>
              {item.agentName && (
                <span className="badge bg-light text-body border fs-10">
                  <i className="ri-user-line me-1 text-primary"></i>
                  {item.agentName}
                </span>
              )}
            </div>
            <h6 className="fs-13 mt-3 mb-2 text-dark fw-semibold d-flex align-items-center gap-1 flex-wrap">
              De{' '}
              <CustomerStateBadge
                state={{
                  name: item.fromStateName || 'Sin Estado',
                  type: item.fromStateType as any,
                }}
              />{' '}
              a{' '}
              <CustomerStateBadge
                state={{
                  name: item.toStateName,
                  type: item.toStateType as any,
                }}
              />
            </h6>
          </div>
        </div>
      ))}
      {isFetchingMore && (
        <div className="acitivity-item d-flex mb-4 placeholder-glow">
          <div className="flex-shrink-0">
            <div className="avatar-xs acitivity-avatar">
              <span className="avatar-title placeholder bg-secondary rounded-circle"></span>
            </div>
          </div>
          <div className="flex-grow-1 ms-3">
            <div className="placeholder bg-secondary col-4 mb-2" style={{ height: '10px' }}></div>
            <div className="placeholder bg-secondary col-8 d-block" style={{ height: '15px' }}></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerStateTimeline;
